import { text } from '$lib/utils/text';
import type { Graph } from 'schema-dts';

export interface JsonLdReview {
	createdAt: {
		input: string;
		raw: Date;
		human: string;
		shortHuman: string;
		time: string;
		shortEnglish: string;
		timeEnglish: string;
	};
	description: string;
	title: string;
	createdBy: string;
	rating: number;
}

interface JsonLdCity {
	city: string;
	country: string;
	lat: number;
	lng: number;
	label: string;
}

export interface JsonLdTaxiService {
	app: {
		name: string;
		url: string;
		phone: string;
		sameAs: string[];
	};
	from: JsonLdCity;
	to: JsonLdCity;
	distance: number;
	duration: number;
	reviews: JsonLdReview[];
	reviewsCount: number;
	reviewsRating: number;
}

export const jsonLd = (data: JsonLdTaxiService): Graph => {
	const { from, to, distance, reviews, app, duration } = data;
	const fromSlug = text.slug(from.city);
	const toSlug = text.slug(to.city);
	const url = `${app.url}/taxi/${fromSlug}/${toSlug}`;
	return {
		'@context': 'https://schema.org',
		'@graph': [
			{
				'@type': 'Product',
				name: `Taxi from ${from.city} to ${to.city}`,
				url,
				image: [`${app.url}/img/og-image.jpg`],
				description: `Professional intercity taxi service from ${from.city} to ${to.city} with comfortable, air-conditioned vehicles, WiFi, and experienced drivers. Door-to-door transportation with flexible pickup times.`,
				brand: {
					'@type': 'Brand',
					name: app.name
				},
				offers: {
					'@type': 'Offer',
					url,
					price: `${Math.round(distance * 1)}`,
					priceCurrency: 'EUR',
					availability: 'https://schema.org/InStock',
					validFrom: new Date().toISOString()
				},
				aggregateRating: {
					'@type': 'AggregateRating',
					ratingValue: data.reviewsRating,
					reviewCount: data.reviewsCount,
					bestRating: 5,
					worstRating: 1
				},
				review:
					reviews.length > 0
						? reviews.map((review) => ({
								'@type': 'Review',
								author: {
									'@type': 'Person',
									name: review.createdBy
								},
								reviewRating: {
									'@type': 'Rating',
									ratingValue: review.rating
								},
								datePublished: new Date(review.createdAt.raw).toISOString(),
								reviewBody: review.description
							}))
						: undefined
			},
			{
				'@type': 'Offer',
				availability: 'https://schema.org/InStock',
				priceSpecification: {
					'@type': 'PriceSpecification',
					priceCurrency: 'EUR',
					price: `${Math.round(distance * 1)}`,
					valueAddedTaxIncluded: true
				}
			},
			{
				'@type': 'TaxiService',
				name: `${app.name} - Taxi from ${from.city} to ${to.city}`,
				url,
				areaServed: [
					{
						'@type': 'City',
						name: from.city,
						geo: {
							'@type': 'GeoCoordinates',
							latitude: from.lat,
							longitude: from.lng
						}
					},
					{
						'@type': 'City',
						name: to.city,
						geo: {
							'@type': 'GeoCoordinates',
							latitude: to.lat,
							longitude: to.lng
						}
					}
				],
				provider: {
					'@type': 'Organization',
					name: app.name,
					url: app.url,
					telephone: app.phone,
					sameAs: app.sameAs
				}
			},
			{
				'@type': 'Event',
				name: `${from.city} to ${to.city} Taxi or Shuttle Service`,
				eventAttendanceMode: 'https://schema.org/OfflineEventAttendanceMode',
				eventStatus: 'https://schema.org/EventScheduled',
				location: {
					'@type': 'Place',
					name: from.city,
					address: {
						'@type': 'PostalAddress',
						addressLocality: from.city,
						addressCountry: getCountryCode(from.country)
					}
				},
				image: [`${app.url}/img/og-image.jpg`],
				duration: getDurationForSchema(duration),
				startDate: new Date(Date.now() + 5 * 24 * 60 * 60 * 1000).toISOString(),
				description: `Private shuttle service from ${from.city} to ${to.city} with comfortable, air-conditioned vehicles, WiFi, and experienced drivers. Door-to-door transportation with flexible pickup times.`,
				offers: {
					'@type': 'Offer',
					url,
					price: `${Math.round(distance * 1)}`,
					priceCurrency: 'EUR',
					availability: 'https://schema.org/InStock',
					validFrom: new Date().toISOString()
				},
				organizer: {
					'@type': 'Organization',
					name: app.name,
					url: app.url
				},
				aggregateRating:
					data.reviewsCount > 0
						? {
								'@type': 'AggregateRating',
								ratingValue: data.reviewsRating,
								reviewCount: data.reviewsCount,
								bestRating: 5,
								worstRating: 1
							}
						: undefined,
				review:
					reviews.length > 0
						? reviews.map((review) => ({
								'@type': 'Review',
								author: {
									'@type': 'Person',
									name: review.createdBy
								},
								reviewRating: {
									'@type': 'Rating',
									ratingValue: review.rating
								},
								datePublished: new Date(review.createdAt.raw).toISOString(),
								reviewBody: review.description
							}))
						: undefined
			}
		].filter(Boolean)
	} as Graph;
};

function getCountryCode(country: string) {
	const countryList: Record<string, string> = {
		Czechia: 'CZ',
		Germany: 'DE',
		Austria: 'AT',
		Slovakia: 'SK',
		Poland: 'PL',
		Hungary: 'HU',
		Slovenia: 'SI',
		Italy: 'IT',
		Spain: 'ES',
		France: 'FR',
		Portugal: 'PT',
		Romania: 'RO',
		Bulgaria: 'BG',
		Greece: 'GR',
		Netherlands: 'NL',
		Belgium: 'BE',
		Luxembourg: 'LU',
		Switzerland: 'CH',
		Sweden: 'SE',
		Norway: 'NO',
		Denmark: 'DK',
		Finland: 'FI',
		Iceland: 'IS',
		Liechtenstein: 'LI',
		Monaco: 'MC',
		'San Marino': 'SM',
		'Vatican City': 'VA'
	};
	return countryList[country] || 'CZ';
}

function getDurationForSchema(duration: number) {
	const hours = Math.floor(duration / 3600);
	const minutes = Math.floor((duration % 3600) / 60);
	return `PT${hours}H${minutes}M`;
}

export function serializeJsonLd(graph: Graph) {
	return `<script type="application/ld+json">${JSON.stringify(graph, null, 2)}</script>`;
}
